/** Polish, pl-PL formatting helpers shared by every screen. */

export function fmtDate(value?: string | null, withTime = false): string {
  if (!value) return "-";
  const date = new Date(value);
  const day = date.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
  if (!withTime) return day;
  return `${day}, ${date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;
}

/** "max 5 · reset 06:00" - the door rules in one glanceable line. */
export function fmtRules(maxEntries: number | null | undefined, resetHour: number | null | undefined): string {
  const parts: string[] = [];
  if (maxEntries) parts.push(`max ${maxEntries}`);
  if (resetHour !== null && resetHour !== undefined) parts.push(`reset ${hour(resetHour)}`);
  return parts.length ? parts.join(" · ") : "-";
}

export const hour = (h: number) => `${String(h).padStart(2, "0")}:00`;

export const num = (n: number) => n.toLocaleString("pl-PL");

/** Polish plural: 1 karta, 2-4 karty, 5+ kart (with the teens exception). */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (n === 1) return one;
  if (last >= 2 && last <= 4 && !(abs >= 12 && abs <= 14)) return few;
  return many;
}

export const ROLE_LABEL: Record<string, string> = {
  admin: "admin",
  staff: "obsługa",
  operator: "operator",
};

export const STATUS_LABEL: Record<string, string> = {
  created: "wydana",
  claimed: "w portfelu",
  revoked: "unieważniona",
  expired: "wygasła",
};

/** Refusal reasons as staff would say them, not as the database stores them. */
export const RESULT_LABEL: Record<string, string> = {
  ok: "Wejście",
  forced: "Wejście: decyzja obsługi",
  manual: "Korekta: dodane wejście",
  void: "Korekta: cofnięte wejście",
  "denied:revoked": "Odmowa: karta unieważniona",
  "denied:expired": "Odmowa: karta wygasła",
  "denied:not-claimed": "Odmowa: karta nieaktywna",
  "denied:max-entries": "Odmowa: limit wejść",
  "denied:cooldown": "Odmowa: za wcześnie",
};

/** Audit actions, narrated for the venue's own trail. */
export function describeAudit(action: string, meta: Record<string, unknown>): string {
  const serial = typeof meta.serial === "string" ? meta.serial.slice(0, 8).toUpperCase() : "";
  switch (action) {
    case "pass.issue":
      return `Wydano kartę ${serial}${meta.tier ? ` · ${String(meta.tier)}` : ""}`;
    case "pass.bulk_issue":
      return `Wydano partię ${String(meta.count ?? "")} kart · ${String(meta.tier ?? "")}`;
    case "pass.revoke":
      return `Unieważniono kartę ${serial}`;
    case "pass.restore":
      return `Przywrócono kartę ${serial}`;
    case "pass.rules":
      return `Zmieniono zasady karty ${serial}`;
    case "pass.note":
      return meta.cleared ? `Usunięto notatkę karty ${serial}` : `Zmieniono notatkę karty ${serial}`;
    case "claim_link.issue":
      return `Nowy kod odbioru dla karty ${serial}`;
    case "tenant.create":
      return "Utworzono lokal";
    case "card.fields":
      return "Zmieniono pola na karcie";
    case "tenant.logo":
      return meta.cleared ? "Usunięto logo lokalu" : "Zmieniono logo lokalu";
    case "tenant.update":
      return "Zmieniono dane lokalu";
    case "tenant.suspend":
      return "Zawieszono lokal";
    case "tenant.activate":
      return "Przywrócono lokal";
    case "user.create":
      return `Dodano konto ${String(meta.email ?? "")}${meta.role ? ` · ${ROLE_LABEL[String(meta.role)] ?? String(meta.role)}` : ""}`;
    case "tier.create":
      return `Dodano poziom ${String(meta.key ?? "")}`;
    case "tier.update":
      return `Zmieniono poziom ${String(meta.key ?? "")}`;
    case "tier.art":
      return meta.cleared ? `Usunięto grafikę poziomu ${String(meta.key ?? "")}` : `Zmieniono grafikę poziomu ${String(meta.key ?? "")}`;
    case "tier.delete":
      return `Usunięto poziom ${String(meta.key ?? "")}`;
    case "scanner_pin.reset":
      return meta.kind === "reward" ? "Zmieniono PIN nagród" : "Zmieniono PIN wejścia";
    case "announcement.set":
      return meta.cleared ? "Usunięto ogłoszenie" : "Opublikowano ogłoszenie";
    case "reward.redeem":
      return `Wydano nagrodę${meta.reward ? ` ${String(meta.reward)}` : ""}${serial ? ` · ${serial}` : ""}`;
    case "pass.adjust_entries": {
      const delta = Number(meta.delta ?? 0);
      return `Korekta wejść ${delta > 0 ? `+${delta}` : delta} · ${serial}${meta.reason ? `, ${String(meta.reason)}` : ""}`;
    }
    default:
      return action;
  }
}

/** "dziś, 00:41" · "wczoraj, 18:10" · "28.08, 16:20" */
export function fmtRelative(value: string): string {
  const date = new Date(value);
  const now = new Date();
  const time = date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(date, now)) return `dziś, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return `wczoraj, ${time}`;
  return `${date.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" })}, ${time}`;
}
